import {
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChevronIcon, RemoveIcon, Search02Icon } from "vega-icons";

import {
  withTooltip,
  type WithTooltipProps,
} from "@vega-core/components/Tooltip/withTooltip";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useInfiniteScroll } from "@vega-core/utils/useInfiniteScroll";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../shadcn/command";

export type SearchItem = {
  id: string;
  searchValue: string;
  content: ReactNode;
};

export type SearchItemGroup<T extends SearchItem> = {
  key: string;
  heading: string;
  items: T[];
};

export type SearchAutocompleteProps<T extends SearchItem> = {
  items: T[];
  groups?: SearchItemGroup<T>[];
  placeholder?: string;
  emptyMessage?: string;
  onChange?: (value: string) => void;
  onItemSelect?: (item: T | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  renderItem?: (item: T) => ReactNode;
  renderSkeleton?: () => ReactNode;
  className?: string;
  showAllOnFocus?: boolean; // Prop to show all options when focused
  showChevron?: boolean; // Prop to show/hide the chevron icon
  name: string;
  compact?: boolean;
  "data-testid"?: string;
  // Infinite scroll props
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  noPadding?: boolean;
  clearOnSelect?: boolean;
};

const DefaultSkeleton = () => (
  <div className="flex flex-col">
    <div className="w-full p-4 border-b border-border/primary">
      <div className="w-full h-9 skeleton-box" />
    </div>
    <div className="w-full p-4">
      <div className="w-full h-9 skeleton-box" />
    </div>
  </div>
);

const BaseSearchAutocomplete = forwardRef(
  <T extends SearchItem>(
    {
      items,
      groups,
      placeholder,
      emptyMessage,
      onChange,
      onItemSelect,
      isLoading = false,
      disabled = false,
      renderItem,
      renderSkeleton,
      className,
      showAllOnFocus = false,
      compact = false,
      showChevron = false,
      name,
      "data-testid": dataTestId,
      // Infinite scroll props
      hasNextPage = false,
      isFetchingNextPage = false,
      onLoadMore,
      noPadding = false,
      clearOnSelect = false,
    }: SearchAutocompleteProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [value, setValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(
      () => () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      },
      [],
    );

    const handleValueChange = (newValue: string) => {
      setValue(newValue);
      onChange?.(newValue);
    };

    const handleSelect = (item: T) => {
      onItemSelect?.(item);

      if (clearOnSelect) {
        handleValueChange("");
      } else {
        handleValueChange(item.searchValue);
      }
      setIsFocused(false);
    };

    // Filter items based on the input value if not empty (use trimmed value so leading/trailing spaces do not hide results)
    const trimmedValue = value.trim();
    const filteredItems = trimmedValue
      ? items.filter((item) =>
          item.searchValue.toLowerCase().includes(trimmedValue.toLowerCase()),
        )
      : items;

    const sentinelElement = useInfiniteScroll(
      () => {
        onLoadMore?.();
      },
      hasNextPage,
      isFetchingNextPage,
    );

    const renderItemContent = (item: T, itemKey: string) => (
      <CommandItem
        className="p-4 rounded-companyBorderRadius text-text/secondary body-base-medium hover:bg-background/secondary hover:text-text/primary"
        data-testid={itemKey}
        key={item.id}
        onSelect={() => handleSelect(item)}
      >
        {renderItem ? renderItem(item) : item.content}
      </CommandItem>
    );

    const renderGroups = (itemGroups: SearchItemGroup<T>[]) => {
      const populatedGroups = itemGroups.filter(
        (group) => group.items.length > 0,
      );

      if (populatedGroups.length === 0) {
        return (
          <CommandEmpty className="p-4 text-text/primary body-base-medium">
            {emptyMessage ?? m.common_noResultsFound()}
          </CommandEmpty>
        );
      }

      return populatedGroups.map((group) => (
        <CommandGroup
          className="p-0 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-text/secondary [&_[cmdk-group-heading]]:body-small-semibold border-b border-border/primary last:border-b-0"
          data-testid={`${name}-search-autocomplete-group-${group.key}`}
          heading={group.heading}
          key={group.key}
        >
          {group.items.map((item, index) =>
            renderItemContent(
              item,
              `${name}-search-autocomplete-${group.key}-item-${index}`,
            ),
          )}
        </CommandGroup>
      ));
    };

    const renderContent = () => {
      if (isLoading) {
        if (renderSkeleton) {
          return renderSkeleton();
        }

        return <DefaultSkeleton />;
      }

      if (groups) {
        return renderGroups(groups);
      }

      // Only show empty message if user has typed something and no results found
      if (value && filteredItems.length === 0) {
        return (
          <CommandEmpty className="p-4 text-text/primary body-base-medium">
            {emptyMessage ?? m.common_noResultsFound()}
          </CommandEmpty>
        );
      }

      return (
        <CommandGroup className="p-0">
          {filteredItems.map((item, index) => (
            <CommandItem
              className={cnTwMerge(
                "p-4 rounded-companyBorderRadius text-text/secondary body-base-medium hover:bg-background/secondary hover:text-text/primary",
                index !== filteredItems.length - 1 &&
                  "border-b border-border/primary",
              )}
              data-testid={`${name}-search-autocomplete-item-${index}`}
              key={item.id}
              onSelect={() => handleSelect(item)}
            >
              {renderItem ? renderItem(item) : item.content}
            </CommandItem>
          ))}
          {/* Render sentinel element for infinite scroll */}
          {hasNextPage ? <div className="ml-36">{sentinelElement}</div> : null}
        </CommandGroup>
      );
    };

    return (
      <Command
        className={cnTwMerge(
          "border-0 overflow-visible rounded-none shadow-none relative",
          { "bg-transparent": compact },
          className,
        )}
        data-testid={dataTestId}
        ref={ref}
        shouldFilter={false}
      >
        <div
          className={cnTwMerge(
            "flex items-center px-3 relative",
            noPadding && "px-0",
          )}
        >
          <Search02Icon
            className={cnTwMerge(
              "h-4 w-4 left-6 text-text/primary absolute",
              noPadding && "left-3",
            )}
          />
          <CommandInput
            className={cnTwMerge(
              "pl-9 bg-background/primary",
              showChevron && "pr-8", // Add right padding when chevron is visible
              disabled && "cursor-not-allowed opacity-50",
            )}
            compact={compact}
            data-testid={`${name}-search-autocomplete-input`}
            disabled={disabled}
            onBlur={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => setIsFocused(false), 250);
            }}
            onFocus={() => (disabled ? null : setIsFocused(true))}
            onValueChange={handleValueChange}
            placeholder={placeholder ?? m.placeholders_search()}
            value={value}
          />
          {value && !disabled ? (
            <RemoveIcon
              className="h-5 w-5 shrink-0 cursor-pointer text-text/secondary absolute rounded-companyBorderRadius bg-background/primary hover:bg-background/secondary right-5"
              data-testid={`${name}-search-autocomplete-clear`}
              onClick={() => {
                handleValueChange("");
                onItemSelect?.(null);
                // Keep dropdown open when clearing to allow immediate re-selection
                setIsFocused(true);
              }}
            />
          ) : null}
          {!value && showChevron ? (
            <ChevronIcon
              className={cnTwMerge(
                "h-4 w-4 shrink-0 text-text/secondary group-hover:text-text/interactive defaultTransition absolute right-5",
                isFocused && "rotate-180",
              )}
            />
          ) : null}
        </div>

        <CommandList
          className={cnTwMerge(
            "rounded-companyBorderRadius z-10 bg-background/primary mx-3 border-1 absolute border-border/primary mt-1 shadow-tooltip w-full top-11 max-h-60 overflow-y-auto",
            // The default mx-3 matches the container's px-3; without padding
            // the list must hug the input instead of overhanging it.
            noPadding && "mx-0",
            // Show dropdown when focused if showAllOnFocus is true, otherwise only show when value exists
            (!isFocused || (!value && !showAllOnFocus) || disabled) && "hidden",
          )}
          data-testid={`${name}-search-autocomplete-list`}
          ref={listRef}
        >
          {renderContent()}
        </CommandList>
      </Command>
    );
  },
);

export const SearchAutocomplete = withTooltip<
  SearchAutocompleteProps<SearchItem>,
  <T extends SearchItem>(
    props: SearchAutocompleteProps<T> & WithTooltipProps,
  ) => ReactNode
>(BaseSearchAutocomplete);
